import {
	Button,
	Checkbox,
	createMuiTheme,
	FormControlLabel,
	MuiThemeProvider,
	TextField,
} from "@material-ui/core";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import Autocomplete from "@material-ui/lab/Autocomplete/Autocomplete";
import React, { useEffect, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";
import {
	Card,
	CardBody,
	CardFooter,
	CardHeader,
	CardHeaderToolbar,
} from "../../../../_metronic/_partials/controls";
import { getRoles } from "../../../../api/role";
import {
	deleteUser,
	getUserById,
	postUser,
	updateUser,
} from "../../../../api/user";
import { checkIsEmpty } from "../../../../utils/helpers";
import { alertError, alertSuccess } from "../../../../utils/logger";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import { useSkeleton } from "../../../hooks/useSkeleton";

// Create theme for delete button (red)
const theme = createMuiTheme({
	palette: {
		secondary: {
			main: "#F64E60",
		},
	},
});

function getEmptyUser() {
	return {
		nombre: null,
		email: null,
		password: null,
		role_id: null,
		notas: null,
		activo: true,
	};
}

export default function EditUsersPage() {
	const [user, setUser] = useState(getEmptyUser());
	const [roles, setRoles] = useState([]);

	const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
	const [newPassword, setNewPassword] = useState({
		password: null,
		repeatPassword: null,
	});
	const [showPassword, setShowPassword] = useState(false);
	const [showRepeatPassword, setShowRepeatPassword] = useState(false);
	const [changePassword, setChangePassword] = useState(false);

	const userId = useParams().id;
	const history = useHistory();
	const location = useLocation();

	const loggedUser = useSelector(
		(store) => store.authentication?.user,
		shallowEqual
	);

	const handleBack = () => {
		const prevTablePage = location.state?.page;

		history.push(
			`/usuarios${prevTablePage ? "?page=" + prevTablePage : ""}`
		);
	};

	const {
		isLoading: isLoadingData,
		disableLoading: disableLoadingData,
		ContentSkeleton,
	} = useSkeleton();

	useEffect(() => {
		getRoles(loggedUser?.accessToken)
			.then((res) => {
				if (res.status === 200) {
					let roles = res.data;
					setRoles(roles);
				}
			})
			.catch((error) => {
				alertError({
					error: error,
					customMessage: "No se pudieron obtener los roles.",
				});
				handleBack();
			});
		if (!userId) {
			disableLoadingData();
			return;
		}
		getUserById(userId, loggedUser?.accessToken)
			.then((res) => {
				if (res.status === 200) {
					setUser(res.data);
					disableLoadingData();
				}
			})
			.catch((error) => {
				alertError({
					error: error,
					customMessage: "No se ha podido obtener el usuario.",
				});
				handleBack();
			});
	}, [userId, disableLoadingData, history]);

	function saveUser() {
		let saveUser = user;
		if (checkIsEmpty(saveUser?.nombre)) {
			alertError({
				error: "El nombre es necesario.",
			});
			return;
		}
		if (checkIsEmpty(saveUser?.email)) {
			alertError({
				error: "El email es necesario.",
			});
			return;
		}
		if (checkIsEmpty(saveUser?.role_id)) {
			alertError({
				error: "El rol es necesario.",
			});
			return;
		}
		if (!userId || changePassword) {
			if (!newPassword.password || !newPassword.repeatPassword) {
				alertError({
					error: "Por favor introduzca la contraseña.",
				});
				return;
			}
			if (!isPasswordValid(newPassword.password)) {
				alertError({
					error:
						"La contraseña debe tener mínimo 8 caracteres, 1 mayúscula, 1 minúscula y 1 número.",
				});
				return;
			}
			if (newPassword.password !== newPassword.repeatPassword) {
				alertError({
					error: "Las contraseñas no coinciden.",
				});
				return;
			}
			saveUser = { ...saveUser, password: newPassword.password };
		}

		if (!userId) {
			postUser(saveUser, loggedUser?.accessToken)
				.then((res) => {
					if (res.status === 201) {
						alertSuccess({
							title: "¡Guardado!",
							customMessage: "Usuario guardado con éxito.",
						});
						handleBack();
					}
				})
				.catch((error) => {
					alertError({
						error: error,
						customMessage: "No se ha podido guardar el usuario.",
					});
				});
		} else {
			updateUser(userId, saveUser, loggedUser?.accessToken)
				.then((res) => {
					if (res.status === 204) {
						alertSuccess({
							title: "¡Guardado!",
							customMessage: "Cambios guardados con éxito.",
						});
						handleBack();
					}
				})
				.catch((error) => {
					alertError({
						error: error,
						customMessage: "No se han podido guardar los cambios.",
					});
				});
		}
	}

	const handleChange = (element) => (event) => {
		setUser({ ...user, [element]: event.target.value });
	};

	const isPasswordValid = (password) => {
		const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
		return passwordRegex.test(password);
	};

	if (isLoadingData) return <ContentSkeleton />;
	else
		return (
			<>
				<Card>
					<CardHeader title="Usuario">
						<CardHeaderToolbar>
							<Button
								onClick={() => saveUser()}
								variant="outlined"
								color="primary"
								style={{ marginRight: userId ? "20px" : "" }}
							>
								Guardar usuario
							</Button>
							{userId && (
								<>
									<MuiThemeProvider theme={theme}>
										<Button
											className="py-2"
											onClick={() =>
												setOpenConfirmDialog(true)
											}
											variant="outlined"
											color="secondary"
										>
											Eliminar usuario
										</Button>
									</MuiThemeProvider>
								</>
							)}
						</CardHeaderToolbar>
					</CardHeader>
					<CardBody>
						<form autoComplete="off">
							<div className="row d-flex align-items-center">
								<div className="col-md-6">
									<TextField
										id={`nombre`}
										label="Nombre"
										value={user.nombre}
										onChange={handleChange("nombre")}
										InputLabelProps={{
											shrink: true,
										}}
										margin="normal"
										variant="outlined"
										required
									/>
								</div>
								<div className="col-md-6">
									<Autocomplete
										id="autocomplete-role"
										disablePortal
										disableClearable
										filterSelectedOptions
										options={roles}
										getOptionLabel={(option) =>
											option.descripcion
										}
										value={
											roles?.find(
												(x) => x.id === user?.role_id
											) || ""
										}
										onChange={(event, selected) => {
											setUser({
												...user,
												role_id: selected
													? selected.id
													: "",
											});
										}}
										renderInput={(params) => (
											<TextField
												{...params}
												label="Rol"
												margin="normal"
												variant="outlined"
												InputLabelProps={{
													shrink: true,
												}}
												required
											/>
										)}
									/>
								</div>
							</div>
							<TextField
								id={`email`}
								label="Email"
								value={user.email}
								onChange={handleChange("email")}
								InputLabelProps={{
									shrink: true,
								}}
								autoComplete="off"
								margin="normal"
								variant="outlined"
								required
							/>
							{!userId || changePassword ? (
								<>
									<div className="row d-flex align-items-center">
										<div className="col-md-6">
											<TextField
												id={`password`}
												label={
													changePassword
														? "Nueva Contraseña"
														: "Contraseña"
												}
												value={newPassword.password}
												onChange={(event) => {
													if (
														event.target.value !==
														" "
													)
														setNewPassword({
															...newPassword,
															password:
																event.target
																	.value,
														});
												}}
												InputLabelProps={{
													shrink: true,
												}}
												type={
													showPassword
														? "text"
														: "password"
												}
												margin="normal"
												variant="outlined"
												required
												autoComplete="off"
												InputProps={{
													endAdornment: (
														<Button
															onClick={() => {
																setShowPassword(
																	!showPassword
																);
															}}
														>
															{showPassword ? (
																<VisibilityOff />
															) : (
																<Visibility />
															)}
														</Button>
													),
												}}
											/>
										</div>
										<div className="col-md-6">
											<TextField
												id={`repeatPassword`}
												label={
													changePassword
														? "Repetir nueva contraseña"
														: "Repetir contraseña"
												}
												value={
													newPassword.repeatPassword
												}
												onChange={(event) => {
													if (
														event.target.value !==
														" "
													)
														setNewPassword({
															...newPassword,
															repeatPassword:
																event.target
																	.value,
														});
												}}
												InputLabelProps={{
													shrink: true,
												}}
												type={
													showRepeatPassword
														? "text"
														: "password"
												}
												margin="normal"
												variant="outlined"
												required
												autoComplete="off"
												InputProps={{
													endAdornment: (
														<Button
															onClick={() => {
																setShowRepeatPassword(
																	!showRepeatPassword
																);
															}}
														>
															{showRepeatPassword ? (
																<VisibilityOff />
															) : (
																<Visibility />
															)}
														</Button>
													),
												}}
											/>
										</div>
									</div>
									{userId && (
										<>
											<br />
											<div className="row d-flex justify-content-start ml-0">
												<div>
													<Button
														onClick={() => {
															setChangePassword(
																false
															);
															setNewPassword({
																password: null,
																repeatPassword: null,
															});
															setShowPassword(
																false
															);
															setShowRepeatPassword(
																false
															);
														}}
														variant="outlined"
														style={{
															marginRight: "20px",
														}}
													>
														Cancelar
													</Button>
												</div>
											</div>
											<br />
										</>
									)}
								</>
							) : (
								<>
									<br />
									<br />
									<Button
										onClick={() => setChangePassword(true)}
										variant="outlined"
										color="primary"
										style={{ marginRight: "20px" }}
									>
										Cambiar contraseña
									</Button>
									<br />
									<br />
								</>
							)}
							<TextField
								id={`notas`}
								label="Notas"
								value={user.notas}
								onChange={handleChange("notas")}
								InputLabelProps={{
									shrink: true,
								}}
								multiline
								rows={5}
								margin="normal"
								variant="outlined"
							/>
						</form>
					</CardBody>
					<CardFooter>
						<FormControlLabel
							control={
								<Checkbox
									checked={user.activo}
									onChange={() =>
										setUser({
											...user,
											activo: !user.activo,
										})
									}
									name="checkActive"
								/>
							}
							label="Activo"
						/>
					</CardFooter>
				</Card>
				<Button
					onClick={handleBack}
					variant="outlined"
					style={{ marginRight: "20px" }}
				>
					Atrás
				</Button>
				<Button
					onClick={() => saveUser()}
					variant="outlined"
					color="primary"
					style={{ marginRight: userId ? "20px" : "" }}
				>
					Guardar usuario
				</Button>
				{userId && (
					<>
						<MuiThemeProvider theme={theme}>
							<Button
								className="py-2"
								onClick={() => setOpenConfirmDialog(true)}
								variant="outlined"
								color="secondary"
							>
								Eliminar usuario
							</Button>
						</MuiThemeProvider>

						<ConfirmDialog
							title={
								"¿Estás seguro de que quieres eliminar este usuario?"
							}
							open={openConfirmDialog}
							setOpen={setOpenConfirmDialog}
							onConfirm={() => {
								deleteUser(userId, loggedUser?.accessToken)
									.then((res) => {
										if (
											res.status === 204 ||
											res.status === 200
										) {
											alertSuccess({
												title: "¡Eliminado!",
												customMessage:
													"Usuario eliminado con éxito",
											});
											handleBack();
										}
									})
									.catch((error) => {
										alertError({
											error: error,
											customMessage:
												"No se ha podido eliminar el usuario.",
										});
									});
							}}
						/>
					</>
				)}
			</>
		);
}
